// Programmatic SaaS landing pages targeting global search + AI-assistant queries.
// Each entry renders a public /solutions/:slug page with unique copy + JSON-LD.

export const SOLUTIONS = [
  {
    slug: "ai-visualization-software",
    keyword: "AI visualization software",
    title: "AI Visualization Software for Home Improvement Sales | Customize Studio",
    metaTitle: "AI Visualization Software for Contractors & Remodelers",
    metaDescription:
      "Customize Studio is AI visualization software that turns a single photo into a photorealistic preview of the finished project in seconds. Built for home improvement sales teams.",
    hero:
      "AI visualization software that closes jobs on the spot",
    subhero:
      "Upload one photo of the customer's home and instantly render a photorealistic before-and-after. No CAD, no designers, no waiting.",
    intro: [
      "Customize Studio is purpose-built AI visualization software for product-based home improvement companies. Instead of describing what a finished kitchen, roof, or bathroom will look like, your reps show it — a photorealistic render generated from a single phone photo in seconds.",
      "Traditional visualization tools require CAD skills, expensive design staff, or slow turnaround from a rendering service. Customize Studio replaces all of that with an AI engine that keeps the customer's real room intact and only swaps in the products you sell.",
    ],
    features: [
      ["Photo-to-render in seconds", "Snap a photo and get a finished-project preview while you're still in the room."],
      ["Your real product catalog", "Load your own colors, finishes, styles and materials so every render is sellable."],
      ["Keeps the original room", "The AI edits only the scope you choose and preserves the customer's actual space."],
      ["Team-ready SaaS", "Unlimited seats, per-rep tracking, and a shared credit balance across your whole company."],
    ],
    howToTitle: "How AI visualization works in Customize Studio",
    howTo: [
      ["Upload a photo", "Take or upload a photo of the customer's room or exterior."],
      ["Pick the products", "Choose the cabinet, color, countertop, siding or material options you sell."],
      ["Generate the render", "The AI produces a photorealistic before-and-after preview in seconds."],
      ["Close the job", "Show the homeowner the finished result and get the yes on the first visit."],
    ],
    faqs: [
      ["What is AI visualization software?", "AI visualization software uses artificial intelligence to generate photorealistic images of a finished project from a photo, so customers can see the result before any work begins. Customize Studio does this for home improvement trades in seconds."],
      ["Do I need CAD or design skills?", "No. Customize Studio requires no CAD or design experience. You upload a photo, pick the products you sell, and the AI renders the finished project automatically."],
      ["What trades does it support?", "It is trade-agnostic. You build your own product catalog, so it works for kitchens, bathrooms, roofing, siding, windows, gutters, showers and more."],
      ["How fast is a render?", "Most renders complete in a few seconds, fast enough to show the homeowner during the sales appointment."],
    ],
  },
  {
    slug: "before-and-after-software",
    keyword: "before and after software",
    title: "Before & After Software for Contractors | Customize Studio",
    metaTitle: "Before & After Software for Contractors & Remodelers",
    metaDescription:
      "Create instant before-and-after images for home improvement sales. Customize Studio is AI before-and-after software that renders the finished project from one photo.",
    hero: "Before & after software that sells the finished job",
    subhero:
      "Turn a plain photo of the customer's home into a stunning before-and-after preview homeowners can't ignore.",
    intro: [
      "Homeowners buy what they can see. Customize Studio is before-and-after software that lets your sales team generate a realistic 'after' image right next to the customer's real 'before' — instantly, on any device.",
      "Use it to justify premium materials, re-engage stalled quotes, and beat competitors who are still waiting on hand-drawn renderings.",
    ],
    features: [
      ["Instant after images", "Render the finished project from the customer's own photo."],
      ["Side-by-side slider", "Present a clean before-and-after comparison that drives decisions."],
      ["Re-engage cold leads", "Send a render that pulls stalled quotes back to life."],
      ["Render history", "Every before-and-after is saved for follow-up and proposals."],
    ],
    howToTitle: "How to make a before & after image",
    howTo: [
      ["Capture the 'before'", "Upload a photo of the current room or exterior."],
      ["Select the upgrade", "Choose the products and finishes for the new look."],
      ["Render the 'after'", "AI generates a photorealistic finished result."],
      ["Compare and close", "Show the before-and-after slider to the homeowner."],
    ],
    faqs: [
      ["What is before and after software?", "Before-and-after software generates an image of what a space will look like after a renovation and shows it next to the original photo. Customize Studio creates these with AI from a single photo."],
      ["Can I use my own products?", "Yes. You load your own catalog of colors, finishes and materials so the 'after' image reflects exactly what you sell."],
      ["Is it good for sales appointments?", "Absolutely. Renders are fast enough to create live in front of the homeowner, which dramatically improves close rates."],
      ["Does it store my before-and-afters?", "Yes, every render is saved in your history for follow-ups and proposals."],
    ],
  },
  {
    slug: "home-improvement-rendering-software",
    keyword: "home improvement rendering software",
    title: "Home Improvement Rendering Software | Customize Studio",
    metaTitle: "Home Improvement Rendering & Image-Making Software",
    metaDescription:
      "Home improvement rendering software powered by AI. Generate photorealistic remodel previews from a photo — no CAD, no designers. Built for remodelers and contractors.",
    hero: "Home improvement rendering software, powered by AI",
    subhero:
      "Photorealistic remodel renders from a single photo — the modern replacement for CAD and costly design services.",
    intro: [
      "Customize Studio is home improvement rendering software that produces photorealistic images of finished projects without CAD software or a design team. Your reps take a photo and the AI renders the remodel using the products you actually sell.",
      "It's image-making software built for the field: fast, mobile-friendly, and priced on a simple credit model so you only pay for the renders you create.",
    ],
    features: [
      ["No CAD required", "Skip the modeling software and the learning curve entirely."],
      ["Photorealistic output", "AI renders look real enough to sell premium scope."],
      ["Mobile-first", "Create renders in the field on a phone or tablet."],
      ["Credit-based pricing", "Pay only for the renders you generate, with team-wide balance."],
    ],
    howToTitle: "How home improvement rendering works",
    howTo: [
      ["Photograph the space", "Upload the customer's room or exterior photo."],
      ["Choose the scope", "Pick the materials, colors and finishes to render."],
      ["Generate", "AI produces a photorealistic remodel image."],
      ["Present the render", "Share it on the spot or in a proposal."],
    ],
    faqs: [
      ["What is home improvement rendering software?", "It is software that creates images of what a remodel will look like when finished. Customize Studio uses AI to render these from a photo, without CAD."],
      ["How is this different from CAD?", "CAD requires 3D modeling skills and time. Customize Studio renders directly from a photo in seconds using AI, with no modeling needed."],
      ["What does it cost?", "Customize Studio uses a credit model. Plans include monthly credits, and you can buy top-up credit packs as needed."],
      ["Who is it for?", "Remodelers, contractors and product-based home improvement companies across every trade."],
    ],
  },
  {
    slug: "ai-kitchen-reface-visualizer",
    keyword: "AI kitchen reface visualizer",
    title: "AI Kitchen Reface & Cabinet Refacing Visualizer | Customize Studio",
    metaTitle: "AI Kitchen Reface & Cabinet Refacing Visualizer",
    metaDescription:
      "See a kitchen reface before you build it. Customize Studio's AI kitchen visualizer renders new cabinets, colors, countertops, hardware and backsplash from one photo.",
    hero: "AI kitchen reface visualizer",
    subhero:
      "Upload a kitchen photo and instantly preview new cabinet doors, colors, countertops, hardware, sinks, faucets and backsplash.",
    intro: [
      "Customize Studio's AI kitchen reface visualizer keeps the customer's real kitchen intact and swaps in the finishes they're considering — cabinet styles, paint colors, countertops, hardware, sinks, faucets and backsplash — in a single photorealistic render.",
      "It's the fastest way for cabinet refacing and kitchen remodel companies to help homeowners commit to a look and approve premium upgrades.",
    ],
    features: [
      ["Cabinet refacing preview", "Swap door styles and colors on the customer's actual cabinets."],
      ["Full kitchen scope", "Countertops, hardware, sinks, faucets and backsplash in one render."],
      ["Realistic lighting", "The AI preserves the room's real lighting and layout."],
      ["Your finish catalog", "Load your own door styles, paints and countertop options."],
    ],
    howToTitle: "How to visualize a kitchen reface",
    howTo: [
      ["Upload the kitchen photo", "Take a photo of the existing kitchen."],
      ["Select finishes", "Pick cabinet style, color, countertop, hardware and backsplash."],
      ["Render the reface", "AI previews the new kitchen while keeping the room intact."],
      ["Approve the look", "The homeowner sees and signs off on the design."],
    ],
    faqs: [
      ["What is an AI kitchen reface visualizer?", "It is a tool that shows what a kitchen will look like after refacing. Customize Studio renders new cabinet doors, colors, countertops and more from a single photo of the existing kitchen."],
      ["Does it keep my customer's real kitchen?", "Yes. The AI preserves the actual room and layout and only changes the finishes you select."],
      ["Can it show countertops and backsplash too?", "Yes, you can render cabinets, countertops, hardware, sinks, faucets and backsplash together."],
      ["Is this for cabinet refacing companies?", "Yes, it's ideal for cabinet refacing and kitchen remodel businesses that want to sell design decisions faster."],
    ],
  },
  {
    slug: "remodel-preview-software-for-sales-teams",
    keyword: "remodel preview software for sales teams",
    title: "AI Remodel Preview Software for Sales Teams | Customize Studio",
    metaTitle: "AI Remodel Preview Software for Sales Teams",
    metaDescription:
      "Give every rep an AI remodel preview tool. Customize Studio renders finished projects from a photo and tracks usage across unlimited seats. Built for home improvement sales teams.",
    hero: "Remodel preview software built for sales teams",
    subhero:
      "Equip every rep with an AI tool that renders the finished remodel from a photo — with unlimited seats and full usage tracking.",
    intro: [
      "Customize Studio is remodel preview software designed for growing home improvement sales teams. Every rep can render photorealistic previews in the field, drawing from one shared company catalog and credit balance.",
      "Owners and managers get role-based access, per-rep usage tracking, and a simple flat plan with unlimited seats — so scaling the team never means scaling per-seat costs.",
    ],
    features: [
      ["Unlimited seats", "Add every rep without per-seat billing surprises."],
      ["Role-based access", "Owner, manager and sales-rep roles keep control clean."],
      ["Per-rep usage tracking", "See exactly how many renders each rep creates."],
      ["Shared catalog & credits", "One company library and credit balance for the whole team."],
    ],
    howToTitle: "How sales teams use remodel preview software",
    howTo: [
      ["Set up your catalog", "Load the products and finishes your company sells."],
      ["Invite your reps", "Add unlimited seats with role-based access."],
      ["Render in the field", "Reps generate previews live during appointments."],
      ["Track and improve", "Managers review usage and close-rate impact."],
    ],
    faqs: [
      ["Is Customize Studio good for teams?", "Yes. It supports unlimited seats, role-based access, and per-rep usage tracking, making it ideal for home improvement sales teams."],
      ["How does seat pricing work?", "The plan includes unlimited seats. You pay a flat subscription plus credits for renders, not per user."],
      ["Can managers track rep activity?", "Yes, owners and managers can see how many renders each rep creates."],
      ["Do reps share one catalog?", "Yes, the whole company draws from one shared product catalog and credit balance."],
    ],
  },
];

export const getSolution = (slug) => SOLUTIONS.find((s) => s.slug === slug);
